"use client";

// ============================================================
// CLUBHUB – Vorkommen einer Serie waehlen
// ============================================================
// Ein Training steht einmal in der Datenbank und zweimal die Woche
// im Kalender. Zusagen, Anwesenheit und Absagen gelten aber einem
// TAG. Diese Leiste unter dem Kopf sagt, welcher Tag gerade offen
// ist, blaettert zum vorigen und naechsten und zeigt die Serie als
// Auswahl. Das Register bleibt beim Wechsel stehen: wer in der
// Anwesenheit blaettert, bleibt in der Anwesenheit.
// ============================================================
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ChevronDown, ChevronLeft, ChevronRight, Repeat } from "lucide-react";
import { PLAETTCHEN, PLAETTCHEN_AUS, Schiene } from "@/components/liste";
import { DropdownItem, DropdownLabel, DropdownMenu } from "@/components/ui/dropdown-menu";
import { formatTagMitWochentag, tagVon, uhrzeit, type DayKey } from "@/lib/kalender/tag";
import type { VorkommenStatus } from "@/lib/kalender/views";
import { cn } from "@/lib/utils";

export function VorkommenLeiste({
  aktuell,
  serie,
  regel,
  heute,
}: {
  aktuell: string;
  serie: { key: string; startsAt: string; status: VorkommenStatus }[];
  regel: string;
  heute: DayKey;
}) {
  const pfad = usePathname();
  const router = useRouter();
  const basis = `/kalender/${aktuell}`;
  const rest = pfad.startsWith(basis) ? pfad.slice(basis.length) : "";
  const i = serie.findIndex((s) => s.key === aktuell);
  const vorher = i > 0 ? serie[i - 1] : undefined;
  const nachher = i >= 0 && i < serie.length - 1 ? serie[i + 1] : undefined;
  const hier = serie[i];
  const naechstes = serie.find((s) => tagVon(s.startsAt) >= heute && s.status !== "abgesagt");
  const ziel = (key: string) => `/kalender/${key}${rest}`;

  return (
    <div className="bg-bg-primary border-border flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-3 py-2">
      <span className="text-caption text-text-secondary inline-flex min-w-0 items-center gap-1.5">
        <Repeat className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">Serie: {regel}</span>
      </span>
      <span className="flex-1" aria-hidden />
      <Schiene>
        {vorher ? (
          <Link href={ziel(vorher.key)} aria-label="Voriges Vorkommen" title={formatTagMitWochentag(tagVon(vorher.startsAt))} className={cn(PLAETTCHEN, PLAETTCHEN_AUS, "w-8 justify-center")}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Link>
        ) : (
          <span className={cn(PLAETTCHEN, "text-text-tertiary w-8 justify-center opacity-40")} aria-hidden>
            <ChevronLeft className="h-4 w-4" />
          </span>
        )}
        <DropdownMenu
          align="left"
          className="max-h-80"
          trigger={
            <button type="button" className={cn(PLAETTCHEN, PLAETTCHEN_AUS, "text-body-sm gap-1.5 px-2.5 tabular-nums")}>
              {hier ? `${formatTagMitWochentag(tagVon(hier.startsAt))}, ${uhrzeit(hier.startsAt)}` : "Vorkommen wählen"}
              <ChevronDown className="text-text-tertiary h-3.5 w-3.5" aria-hidden />
            </button>
          }
        >
          <DropdownLabel>Vorkommen der Serie</DropdownLabel>
          {serie.map((s) => (
            <DropdownItem key={s.key} onClick={() => router.push(ziel(s.key))} className={cn("tabular-nums", s.key === aktuell && "font-medium")}>
                <span className={cn("min-w-0 flex-1", s.status === "abgesagt" && "text-text-tertiary line-through")}>
                  {formatTagMitWochentag(tagVon(s.startsAt))}, {uhrzeit(s.startsAt)}
                </span>
                {s.status === "verschoben" && <span className="text-caption text-warning">verschoben</span>}
                {s.status === "abgesagt" && <span className="text-caption text-danger">abgesagt</span>}
                {tagVon(s.startsAt) === heute && <span className="text-caption text-accent">heute</span>}
            </DropdownItem>
          ))}
        </DropdownMenu>
        {nachher ? (
          <Link href={ziel(nachher.key)} aria-label="Nächstes Vorkommen" title={formatTagMitWochentag(tagVon(nachher.startsAt))} className={cn(PLAETTCHEN, PLAETTCHEN_AUS, "w-8 justify-center")}>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        ) : (
          <span className={cn(PLAETTCHEN, "text-text-tertiary w-8 justify-center opacity-40")} aria-hidden>
            <ChevronRight className="h-4 w-4" />
          </span>
        )}
      </Schiene>
      {naechstes && naechstes.key !== aktuell && (
        <Link href={ziel(naechstes.key)} className="text-caption text-accent hover:text-accent-hover font-medium underline-offset-2 hover:underline">
          Zum nächsten
        </Link>
      )}
    </div>
  );
}
